import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Calendar,
  Users,
  AlertTriangle,
  Info,
  Star,
  Zap,
  Settings,
  Save,
  X,
  RefreshCw,
  BarChart3,
  Clock,
  CheckCircle,
  XCircle,
  Megaphone
} from 'lucide-react';

const AdminAnnouncementsPanel = () => {
  const { user, token } = useContext(AuthContext);
  
  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, expired: 0 });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'general',
    priority: 3,
    color: 'bg-blue-500',
    icon: 'Info',
    scheduledTime: '',
    expiresAt: '',
    targetRoles: ['all'],
    isActive: true
  });

  const announcementTypes = {
    maintenance: { 
      label: 'System Maintenance', 
      color: 'bg-red-500', 
      icon: 'AlertTriangle',
      description: 'Scheduled downtime, updates, repairs'
    },
    policy: { 
      label: 'Policy Update', 
      color: 'bg-blue-500', 
      icon: 'Users',
      description: 'HR policies, work guidelines, procedures'
    },
    feature: { 
      label: 'New Feature', 
      color: 'bg-green-500', 
      icon: 'Zap',
      description: 'System updates, new tools, enhancements'
    },
    emergency: { 
      label: 'Emergency Alert', 
      color: 'bg-red-600', 
      icon: 'AlertTriangle',
      description: 'Urgent notifications requiring immediate attention'
    },
    celebration: { 
      label: 'Achievement/News', 
      color: 'bg-yellow-500', 
      icon: 'Star',
      description: 'Team achievements, company news, celebrations'
    },
    general: { 
      label: 'General Notice', 
      color: 'bg-gray-500', 
      icon: 'Info',
      description: 'General information and updates'
    }
  };

  const roleOptions = [
    { value: 'all', label: 'All Users', icon: '👥' },
    { value: 'manager', label: 'Managers Only', icon: '👔' },
    { value: 'supervisor', label: 'Supervisors Only', icon: '👩‍💼' },
    { value: 'agent', label: 'Agents Only', icon: '🎧' },
    { value: 'Pasig', label: 'Pasig Office', icon: '🏢' }
  ];

  const priorityOptions = [
    { value: 1, label: 'Critical', emoji: '🚨', color: 'text-red-600' },
    { value: 2, label: 'High', emoji: '⚠️', color: 'text-orange-600' },
    { value: 3, label: 'Normal', emoji: '📢', color: 'text-blue-600' },
    { value: 4, label: 'Low', emoji: '💡', color: 'text-green-600' },
    { value: 5, label: 'Info', emoji: '🎯', color: 'text-gray-600' }
  ];

  const getAuthHeaders = () => {
    const authToken = token || localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken || ''}`
    };
  };

  const showNotification = (message, type = 'success') => {
    if (type === 'success') {
      setSuccess(message);
      setError(null);
    } else {
      setError(message);
      setSuccess(null);
    }
    setTimeout(() => {
      setSuccess(null);
      setError(null);
    }, 5000);
  };

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const res = await fetch('http://localhost:5000/api/admin/announcements', {
        headers: getAuthHeaders()
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`HTTP ${res.status}: ${res.statusText} - ${errorText}`);
      }
      
      const data = await res.json();
      setAnnouncements(data.announcements || []);
    } catch (err) {
      showNotification(`Failed to fetch announcements: ${err.message}`, 'error');
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await fetch('http://localhost:5000/api/admin/announcements/stats', {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setStats(data.stats || { total: 0, active: 0, expired: 0 });
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = editingId 
        ? `http://localhost:5000/api/admin/announcements/${editingId}`
        : 'http://localhost:5000/api/admin/announcements';
      
      const method = editingId ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...formData,
          createdBy: user?.email || user?.username || user?.name || 'Unknown'
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to save announcement');
      }

      await fetchAnnouncements();
      await fetchStats();
      resetForm();
      showNotification(
        editingId ? 'Announcement updated successfully!' : 'Announcement created successfully!'
      );
    } catch (err) {
      showNotification(err.message, 'error');
    }
    setLoading(false);
  };

  const handleEdit = (announcement) => {
    setFormData({
      title: announcement.title,
      message: announcement.message,
      type: announcement.type,
      priority: announcement.priority,
      color: announcement.color,
      icon: announcement.icon,
      scheduledTime: announcement.scheduledTime ? 
        new Date(announcement.scheduledTime).toISOString().slice(0, 16) : '',
      expiresAt: announcement.expiresAt ? 
        new Date(announcement.expiresAt).toISOString().slice(0, 16) : '',
      targetRoles: announcement.targetRoles || ['all'],
      isActive: announcement.isActive
    });
    setEditingId(announcement._id || announcement.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement? This action cannot be undone.')) {
      return;
    }

    try {
      const res = await fetch(`http://localhost:5000/api/admin/announcements/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!res.ok) {
        throw new Error('Failed to delete announcement');
      }

      await fetchAnnouncements();
      await fetchStats();
      showNotification('Announcement deleted successfully!');
    } catch (err) {
      showNotification('Failed to delete announcement', 'error');
    }
  };

  const toggleActive = async (id, currentStatus) => {
    try {
      const res = await fetch(`http://localhost:5000/api/admin/announcements/${id}/toggle`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ isActive: !currentStatus })
      });

      if (!res.ok) {
        throw new Error('Failed to update announcement status');
      }

      await fetchAnnouncements();
      await fetchStats();
      showNotification(
        `Announcement ${!currentStatus ? 'activated' : 'deactivated'} successfully!`
      );
    } catch (err) {
      showNotification('Failed to update announcement status', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      message: '',
      type: 'general',
      priority: 3,
      color: 'bg-blue-500',
      icon: 'Info',
      scheduledTime: '',
      expiresAt: '',
      targetRoles: ['all'],
      isActive: true
    });
    setEditingId(null);
    setShowForm(false);
  };

  const cleanupExpired = async () => {
    if (!window.confirm('Are you sure you want to delete all expired announcements?')) {
      return;
    }

    try {
      const res = await fetch('http://localhost:5000/api/admin/announcements/cleanup', {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!res.ok) {
        throw new Error('Failed to cleanup expired announcements');
      }

      const data = await res.json();
      await fetchAnnouncements();
      await fetchStats();
      showNotification(`Successfully cleaned up ${data.deletedCount} expired announcements!`);
    } catch (err) {
      showNotification('Failed to cleanup expired announcements', 'error');
    }
  };

  const IconComponent = ({ iconName, className = "w-4 h-4" }) => {
    const icons = {
      AlertTriangle: AlertTriangle,
      Info: Info,
      Star: Star,
      Zap: Zap,
      Users: Users,
      Calendar: Calendar,
      Settings: Settings
    };
    const Icon = icons[iconName] || Info;
    return <Icon className={className} />;
  };

  // Effect to fetch data when component mounts
  useEffect(() => {
    if (['manager', 'admin'].includes(user?.role) && (token || localStorage.getItem('token'))) {
      fetchAnnouncements();
      fetchStats();
    }
  }, [user?.role, token]);

  // Authorization check
  const isAuthorized = ['manager', 'admin'].includes(user?.role);

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
              <div>
                <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">Access Denied</h3>
                <p className="text-red-700 dark:text-red-300">
                  This panel is only available to managers and administrators.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
   <div className="min-h-screen">
  <div className="p-6 pt-20 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <Megaphone className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Announcement Management
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Create and manage system-wide announcements for all users
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { fetchAnnouncements(); fetchStats(); }}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={() => setShowForm(!showForm)}
                className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                New Announcement
              </button>
            </div>
          </div>
        </div>

        {/* Notifications */}
        {(success || error) && (
          <div className={`mb-6 p-4 rounded-lg border ${
            success 
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
          }`}>
            <div className="flex items-center gap-3">
              {success ? (
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
              )}
              <span className={`font-medium ${
                success 
                  ? 'text-green-800 dark:text-green-200' 
                  : 'text-red-800 dark:text-red-200'
              }`}>
                {success || error}
              </span>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
              </div>
              <BarChart3 className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active</p>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              </div>
              <Eye className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Expired</p>
                <p className="text-2xl font-bold text-red-600">{stats.expired}</p>
              </div>
              <Clock className="w-8 h-8 text-red-600" />
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <button
              onClick={cleanupExpired}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              disabled={loading || stats.expired === 0}
            >
              <Trash2 className="w-4 h-4" />
              Cleanup Expired
            </button>
          </div>
        </div>

        {/* Create/Edit Form */}
        {showForm && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingId ? 'Edit Announcement' : 'Create New Announcement'}
              </h2>
              <button 
                onClick={resetForm} 
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Title */}
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-yellow-500 focus:border-yellow-500 dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Scheduled Maintenance Tonight"
                  />
                </div>

                {/* Message */}
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-yellow-500 focus:border-yellow-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Detailed announcement message..."
                  />
                </div>

                {/* Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Type *
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => {
                      const type = e.target.value;
                      const typeConfig = announcementTypes[type];
                      setFormData({
                        ...formData, 
                        type,
                        color: typeConfig.color,
                        icon: typeConfig.icon
                      });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-yellow-500 focus:border-yellow-500 dark:bg-gray-700 dark:text-white"
                  >
                    {Object.entries(announcementTypes).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {announcementTypes[formData.type]?.description}
                  </p>
                </div>

                {/* Priority */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-yellow-500 focus:border-yellow-500 dark:bg-gray-700 dark:text-white"
                  >
                    {priorityOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.emoji} {option.label} ({option.value})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Target Roles */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Target Audience
                  </label>
                  <select
                    multiple
                    value={formData.targetRoles}
                    onChange={(e) => setFormData({
                      ...formData, 
                      targetRoles: Array.from(e.target.selectedOptions, option => option.value)
                    })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-yellow-500 focus:border-yellow-500 dark:bg-gray-700 dark:text-white h-24"
                  >
                    {roleOptions.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.icon} {role.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Hold Ctrl/Cmd to select multiple roles
                  </p>
                </div>

                {/* Scheduled Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Scheduled Time (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.scheduledTime}
                    onChange={(e) => setFormData({...formData, scheduledTime: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-yellow-500 focus:border-yellow-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                {/* Expires At */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Expires At (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.expiresAt}
                    onChange={(e) => setFormData({...formData, expiresAt: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-yellow-500 focus:border-yellow-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Save className="w-4 h-4" />
                  {loading ? 'Saving...' : (editingId ? 'Update' : 'Create')} Announcement
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Announcements List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              All Announcements ({announcements.length})
            </h3>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto"></div>
              <p className="mt-4 text-gray-500 dark:text-gray-400">Loading announcements...</p>
            </div>
          ) : announcements.length === 0 ? (
            <div className="p-12 text-center">
              <Megaphone className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No announcements found
              </h4>
              <p className="text-gray-500 dark:text-gray-400">
                Create your first announcement to get started.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {announcements.map((announcement) => {
                const typeConfig = announcementTypes[announcement.type] || announcementTypes.general;
                const priorityConfig = priorityOptions.find(p => p.value === announcement.priority);
                
                return (
                  <div key={announcement._id || announcement.id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-3">
                          <div className={`${announcement.color} p-3 rounded-full text-white`}>
                            <IconComponent iconName={announcement.icon} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {announcement.title}
                              </h4>
                              {!announcement.isActive && (
                                <span className="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-full">
                                  Inactive
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                              <span className={`px-2 py-1 rounded-full text-white text-xs ${announcement.color}`}>
                                {typeConfig.label}
                              </span>
                              <span className={`font-medium ${priorityConfig?.color || 'text-gray-600'}`}>
                                {priorityConfig?.emoji} Priority {announcement.priority}
                              </span>
                              <span>👥 {announcement.targetRoles?.join(', ') || 'All'}</span>
                              <span>👤 {announcement.createdBy}</span>
                            </div>
                          </div>
                        </div>
                        
                        <p className="text-gray-700 dark:text-gray-300 mb-3 leading-relaxed">
                          {announcement.message}
                        </p>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                          {announcement.scheduledTime && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              Scheduled: {new Date(announcement.scheduledTime).toLocaleString()}
                            </span>
                          )}
                          {announcement.expiresAt && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              Expires: {new Date(announcement.expiresAt).toLocaleString()}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Info className="w-4 h-4" />
                            Created: {new Date(announcement.createdAt || announcement.updatedAt).toLocaleString()}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 ml-6">
                        <button
                          onClick={() => toggleActive(announcement._id || announcement.id, announcement.isActive)}
                          className={`p-2 rounded-lg transition-colors ${
                            announcement.isActive 
                              ? 'text-green-600 hover:bg-green-100 dark:hover:bg-green-900/20' 
                              : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                          title={announcement.isActive ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                        >
                          {announcement.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleEdit(announcement)}
                          className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="View announcement"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(announcement._id || announcement.id)}
                          className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Delete announcement"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAnnouncementsPanel;